// Global Instructions Rule Applied!

import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import {
  CommsTopicGridModel,
  validateCommsTopicGridModel
} from '../../../state_manager/data_models/comms_topic_grid_model.ts'
import {
  AccountTopicsModel,
  validateAccountTopicsModel
} from '../../../state_manager/data_models/account_topics_grid_model.ts'
import {
  BulkAccountTopicsModel,
  BulkFilterOptionsModel,
  validateBulkAccountTopicsModel,
  validateBulkFilterOptionsModel
} from '../../../state_manager/data_models/bulk_account_topics_grid_model.ts'
import {
  DistributionListGridModel,
  DistributionListFilterOptionsModel,
  validateDistributionListGridModel,
  validateDistributionListFilterOptionsModel
} from '../../../state_manager/data_models/distribution_list_grid_model.ts'
import {
  DistributionListRecipient,
  validateDistributionListRecipient
} from '../../../state_manager/data_models/distribution_list_recipients_model.ts'
import {
  FilterValue,
  SortConfig,
  TopicsPaginatedResponse,
  AccountTopicsSavePayload,
  BulkAccountTopicsFilters,
  BulkAccountTopicsSavePayload,
  DistributionListsPaginatedResponse,
  DistributionListSavePayload,
  DistributionListResolvePayload,
  DistributionListResolveResponse
} from '../../../state_manager/interfaces/distribution_lists.ts'
import { readJsonBody } from '../../../core/utils/utilities.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

export type { TopicsPaginatedResponse }

export interface TopicWriteResponse {
  data: { comms_topics__id: string } | null
  error?: string
}

export const fetchTopicsFiltered = async (
  sorting: SortConfig[] = [],
  filters: FilterValue[] = [],
  page: number = 1,
  pageSize: number | null = null,
  accessToken: string | null = null
): Promise<TopicsPaginatedResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const requestBody = {
      data: {
        page,
        page_size: pageSize ?? 200,
        sorting: sorting.map((s) => ({ key: s.key, direction: s.direction })),
        filters: filters.map((f) => ({ key: f.key, value: f.value }))
      }
    }

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DISTRIBUTION_LISTS.TOPICS_FILTER), {
      method: API_ROUTES.DISTRIBUTION_LISTS.TOPICS_FILTER.method,
      headers,
      body: JSON.stringify(requestBody)
    })

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch topics: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown[]; has_more?: boolean; count?: number }
    const rawRows = Array.isArray(json.data) ? json.data : []

    // Validate every row before it reaches Redux; drop (with a warning) any
    // row that fails rather than failing the whole grid.
    const validated: CommsTopicGridModel[] = []
    for (const row of rawRows) {
      try {
        validated.push(validateCommsTopicGridModel(row))
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Topic row failed validation:', e, row)
        }
      }
    }

    return {
      data: validated,
      hasMore: Boolean(json.has_more),
      totalCount: typeof json.count === 'number' ? json.count : validated.length
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error fetching topics:', error)
    }
    return {
      data: [],
      hasMore: false,
      totalCount: 0,
      error: error instanceof Error ? error.message : 'An unexpected error occurred while fetching topics'
    }
  }
}

const writeTopic = async (
  url: string,
  method: string,
  label: string,
  accountRoleChoiceIds: string[],
  accessToken: string | null
): Promise<TopicWriteResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(url, {
      method,
      headers,
      body: JSON.stringify({ data: { label, account_role_choice_ids: accountRoleChoiceIds } })
    })

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const body = await readJsonBody<{ message?: string }>(response)
      throw new Error(body?.message || `Request failed: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: { comms_topics__id: string } | null }
    return { data: json.data ?? null }
  } catch (error) {
    return {
      data: null,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

export const createTopic = async (
  label: string,
  accountRoleChoiceIds: string[],
  accessToken: string | null = null
): Promise<TopicWriteResponse> =>
  writeTopic(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DISTRIBUTION_LISTS.TOPICS_CREATE),
    API_ROUTES.DISTRIBUTION_LISTS.TOPICS_CREATE.method,
    label,
    accountRoleChoiceIds,
    accessToken
  )

export const updateTopic = async (
  topicId: string,
  label: string,
  accountRoleChoiceIds: string[],
  accessToken: string | null = null
): Promise<TopicWriteResponse> =>
  writeTopic(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DISTRIBUTION_LISTS.TOPICS_UPDATE, { topicId }),
    API_ROUTES.DISTRIBUTION_LISTS.TOPICS_UPDATE.method,
    label,
    accountRoleChoiceIds,
    accessToken
  )

// ── Phase 2 — Account Topic tab ────────────────────────────────────────────

export interface AccountTopicsResponse {
  data: AccountTopicsModel | null
  error?: string
}

export const fetchAccountTopics = async (
  accountId: string,
  accessToken: string | null = null
): Promise<AccountTopicsResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DISTRIBUTION_LISTS.ACCOUNT_TOPICS_GET, { accountId }),
      { method: API_ROUTES.DISTRIBUTION_LISTS.ACCOUNT_TOPICS_GET.method, headers }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch account topics: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown }
    if (json.data == null) return { data: null }

    // Validate before it reaches the tab (a response that fails validation
    // is an error, not something to coerce — CLAUDE.md).
    const validated = validateAccountTopicsModel(json.data)
    return { data: validated }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error fetching account topics:', error)
    }
    return {
      data: null,
      error: error instanceof Error ? error.message : 'An unexpected error occurred while fetching account topics'
    }
  }
}

export const saveAccountTopics = async (
  accountId: string,
  payload: AccountTopicsSavePayload,
  accessToken: string | null = null
): Promise<{ success: boolean; error?: string }> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DISTRIBUTION_LISTS.ACCOUNT_TOPICS_SAVE, { accountId }),
      {
        method: API_ROUTES.DISTRIBUTION_LISTS.ACCOUNT_TOPICS_SAVE.method,
        headers,
        body: JSON.stringify({ data: payload })
      }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const body = await readJsonBody<{ message?: string }>(response)
      throw new Error(body?.message || `Failed to save account topics: ${response.status} ${response.statusText}`)
    }
    return { success: true }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

// ── Phase 3 — Bulk Topic Subscription Manager ──────────────────────────────

export interface BulkAccountTopicsResponse {
  data: BulkAccountTopicsModel | null
  error?: string
}

export const fetchBulkAccountTopics = async (
  filters: BulkAccountTopicsFilters,
  page: number = 1,
  pageSize: number = 50,
  accessToken: string | null = null
): Promise<BulkAccountTopicsResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const requestBody = {
      data: {
        role_ids: filters.role_ids ?? [],
        fund_ids: filters.fund_ids ?? [],
        lp_status_ids: filters.lp_status_ids ?? [],
        search: filters.search ?? '',
        page,
        page_size: pageSize
      }
    }

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DISTRIBUTION_LISTS.BULK_ACCOUNT_TOPICS_FILTER),
      {
        method: API_ROUTES.DISTRIBUTION_LISTS.BULK_ACCOUNT_TOPICS_FILTER.method,
        headers,
        body: JSON.stringify(requestBody)
      }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch bulk account topics: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown }
    if (json.data == null) return { data: null }

    // Validate before it reaches the screen (a response that fails
    // validation is an error, not something to coerce — CLAUDE.md).
    const validated = validateBulkAccountTopicsModel(json.data)
    return { data: validated }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error fetching bulk account topics:', error)
    }
    return {
      data: null,
      error:
        error instanceof Error
          ? error.message
          : 'An unexpected error occurred while fetching bulk account topics'
    }
  }
}

export interface BulkFilterOptionsResponse {
  data: BulkFilterOptionsModel | null
  error?: string
}

export const fetchBulkFilterOptions = async (
  accessToken: string | null = null
): Promise<BulkFilterOptionsResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DISTRIBUTION_LISTS.BULK_ACCOUNT_TOPICS_FILTER_OPTIONS),
      {
        method: API_ROUTES.DISTRIBUTION_LISTS.BULK_ACCOUNT_TOPICS_FILTER_OPTIONS.method,
        headers
      }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch filter options: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown }
    if (json.data == null) return { data: null }

    const validated = validateBulkFilterOptionsModel(json.data)
    return { data: validated }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error fetching bulk filter options:', error)
    }
    return {
      data: null,
      error:
        error instanceof Error
          ? error.message
          : 'An unexpected error occurred while fetching filter options'
    }
  }
}

export const saveBulkAccountTopics = async (
  payload: BulkAccountTopicsSavePayload,
  accessToken: string | null = null
): Promise<{ success: boolean; error?: string }> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DISTRIBUTION_LISTS.BULK_ACCOUNT_TOPICS_SAVE),
      {
        method: API_ROUTES.DISTRIBUTION_LISTS.BULK_ACCOUNT_TOPICS_SAVE.method,
        headers,
        body: JSON.stringify({ data: payload })
      }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const body = await readJsonBody<{ message?: string }>(response)
      throw new Error(body?.message || `Failed to save bulk account topics: ${response.status} ${response.statusText}`)
    }
    return { success: true }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

export const deleteTopic = async (
  topicId: string,
  accessToken: string | null = null
): Promise<{ success: boolean; error?: string }> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DISTRIBUTION_LISTS.TOPICS_DELETE, { topicId }),
      { method: API_ROUTES.DISTRIBUTION_LISTS.TOPICS_DELETE.method, headers }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const body = await readJsonBody<{ message?: string }>(response)
      throw new Error(body?.message || `Failed to delete topic: ${response.status} ${response.statusText}`)
    }
    return { success: true }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

// ── Phase 4 — Distribution List Definition ─────────────────────────────────

export interface DistributionListWriteResponse {
  data: { distribution_lists__id: string } | null
  error?: string
}

export interface DistributionListFilterOptionsResponse {
  data: DistributionListFilterOptionsModel | null
  error?: string
}

export const fetchDistributionListsFiltered = async (
  sorting: SortConfig[] = [],
  filters: FilterValue[] = [],
  page: number = 1,
  pageSize: number | null = null,
  accessToken: string | null = null
): Promise<DistributionListsPaginatedResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const requestBody = {
      data: {
        page,
        page_size: pageSize ?? 200,
        sorting: sorting.map((s) => ({ key: s.key, direction: s.direction })),
        filters: filters.map((f) => ({ key: f.key, value: f.value }))
      }
    }

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DISTRIBUTION_LISTS.LISTS_FILTER),
      {
        method: API_ROUTES.DISTRIBUTION_LISTS.LISTS_FILTER.method,
        headers,
        body: JSON.stringify(requestBody)
      }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch distribution lists: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown[]; has_more?: boolean; count?: number }
    const rawRows = Array.isArray(json.data) ? json.data : []

    // Validate every row before it reaches the screen; drop (with a warning)
    // any row that fails rather than failing the whole grid.
    const validated: DistributionListGridModel[] = []
    for (const row of rawRows) {
      try {
        validated.push(validateDistributionListGridModel(row))
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Distribution list row failed validation:', e, row)
        }
      }
    }

    return {
      data: validated,
      hasMore: Boolean(json.has_more),
      totalCount: typeof json.count === 'number' ? json.count : validated.length
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error fetching distribution lists:', error)
    }
    return {
      data: [],
      hasMore: false,
      totalCount: 0,
      error:
        error instanceof Error
          ? error.message
          : 'An unexpected error occurred while fetching distribution lists'
    }
  }
}

export const fetchDistributionListFilterOptions = async (
  accessToken: string | null = null
): Promise<DistributionListFilterOptionsResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DISTRIBUTION_LISTS.LISTS_FILTER_OPTIONS),
      {
        method: API_ROUTES.DISTRIBUTION_LISTS.LISTS_FILTER_OPTIONS.method,
        headers
      }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch filter options: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown }
    if (json.data == null) return { data: null }

    const validated = validateDistributionListFilterOptionsModel(json.data)
    return { data: validated }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error fetching distribution list filter options:', error)
    }
    return {
      data: null,
      error:
        error instanceof Error
          ? error.message
          : 'An unexpected error occurred while fetching filter options'
    }
  }
}

export interface DistributionListDetailResponse {
  data: DistributionListGridModel | null
  error?: string
}

export const fetchDistributionListDetail = async (
  listId: string,
  accessToken: string | null = null
): Promise<DistributionListDetailResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DISTRIBUTION_LISTS.LISTS_DETAIL, { listId }),
      { method: API_ROUTES.DISTRIBUTION_LISTS.LISTS_DETAIL.method, headers }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch distribution list: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown }
    if (json.data == null) return { data: null }

    // Validate before it reaches the form (a response that fails validation
    // is an error, not something to coerce — CLAUDE.md).
    const validated = validateDistributionListGridModel(json.data)
    return { data: validated }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error fetching distribution list detail:', error)
    }
    return {
      data: null,
      error:
        error instanceof Error
          ? error.message
          : 'An unexpected error occurred while fetching the distribution list'
    }
  }
}

const writeDistributionList = async (
  url: string,
  method: string,
  payload: DistributionListSavePayload,
  accessToken: string | null
): Promise<DistributionListWriteResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(url, {
      method,
      headers,
      body: JSON.stringify({ data: payload })
    })

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const body = await readJsonBody<{ message?: string }>(response)
      throw new Error(body?.message || `Request failed: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: { distribution_lists__id: string } | null }
    return { data: json.data ?? null }
  } catch (error) {
    return {
      data: null,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

export const createDistributionList = async (
  payload: DistributionListSavePayload,
  accessToken: string | null = null
): Promise<DistributionListWriteResponse> =>
  writeDistributionList(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DISTRIBUTION_LISTS.LISTS_CREATE),
    API_ROUTES.DISTRIBUTION_LISTS.LISTS_CREATE.method,
    payload,
    accessToken
  )

export const updateDistributionList = async (
  listId: string,
  payload: DistributionListSavePayload,
  accessToken: string | null = null
): Promise<DistributionListWriteResponse> =>
  writeDistributionList(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DISTRIBUTION_LISTS.LISTS_UPDATE, { listId }),
    API_ROUTES.DISTRIBUTION_LISTS.LISTS_UPDATE.method,
    payload,
    accessToken
  )

// ── Phase 5 — Distribution List Usage ("Send Communication") ───────────────

export const resolveDistributionList = async (
  payload: DistributionListResolvePayload,
  accessToken: string | null = null
): Promise<DistributionListResolveResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DISTRIBUTION_LISTS.USAGE_RESOLVE),
      {
        method: API_ROUTES.DISTRIBUTION_LISTS.USAGE_RESOLVE.method,
        headers,
        body: JSON.stringify({ data: payload })
      }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to resolve distribution list: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown[] }
    const rawRows = Array.isArray(json.data) ? json.data : []

    // Validate every row before it reaches the screen; drop (with a warning)
    // any row that fails rather than failing the whole resolution.
    const validated: DistributionListRecipient[] = []
    for (const row of rawRows) {
      try {
        validated.push(validateDistributionListRecipient(row))
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Recipient row failed validation:', e, row)
        }
      }
    }

    return { data: validated }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error resolving distribution list:', error)
    }
    return {
      data: [],
      error:
        error instanceof Error
          ? error.message
          : 'An unexpected error occurred while resolving the distribution list'
    }
  }
}

export const deleteDistributionList = async (
  listId: string,
  accessToken: string | null = null
): Promise<{ success: boolean; error?: string }> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DISTRIBUTION_LISTS.LISTS_DELETE, { listId }),
      { method: API_ROUTES.DISTRIBUTION_LISTS.LISTS_DELETE.method, headers }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const body = await readJsonBody<{ message?: string }>(response)
      throw new Error(
        body?.message || `Failed to delete distribution list: ${response.status} ${response.statusText}`
      )
    }
    return { success: true }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}
